import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Eye, Calendar, MapPin, Pin, Star,
  Facebook, Linkedin, Mail, Copy as CopyIcon, Check, Share2, Play,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const fmt = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return d; } };

// Extract YouTube video ID from various URL formats
function ytId(url) {
  if (!url) return null;
  try {
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=|shorts\/))([\w-]{6,})/);
    return m ? m[1] : null;
  } catch { return null; }
}

export default function NewsDetailPage() {
  const { slug } = useParams();
  const [n, setN] = useState(null);
  const [err, setErr] = useState(null);
  const [related, setRelated] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setN(null); setErr(null);
    api.get(`/news/${slug}`).then((r) => setN(r.data)).catch(() => setErr("Article introuvable"));
    api.get("/news?limit=6").then((r) => setRelated((r.data || []).filter((x) => x.slug !== slug).slice(0, 3)));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  // SEO / meta
  useEffect(() => {
    if (!n) return;
    document.title = `${n.seo_title || n.title} · Front Uni RDC`;
    // Description
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = n.seo_description || n.excerpt || "";
    // Open Graph
    upsertMeta("og:title", n.seo_title || n.title);
    upsertMeta("og:description", n.seo_description || n.excerpt || "");
    upsertMeta("og:image", n.seo_image || n.cover_image || "");
    upsertMeta("og:type", "article");
    return () => { document.title = "Front Uni pour la RDC"; };
  }, [n]);

  const shareUrl = useMemo(() => {
    if (!n) return "";
    // URL universelle avec balises Open Graph dynamiques (Facebook, WhatsApp, X…)
    return `https://frontunirdc.be/api/share/news/${n.slug}`;
  }, [n]);

  if (err) return <div className="p-12 text-center text-foreground/70">{err} — <Link to="/actualites" className="text-ink underline">retour</Link></div>;
  if (!n) return <div className="p-16 text-foreground/60 text-center">Chargement…</div>;

  const videos = (n.youtube_urls && n.youtube_urls.length ? n.youtube_urls : (n.youtube_url ? [n.youtube_url] : [])).filter(Boolean);
  const gallery = (n.images || []).map((img) => typeof img === "string" ? { url: img } : img);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); toast.success("Lien copié");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Copie impossible"); }
  };

  return (
    <article className="bg-bone" data-testid="news-detail-page">
      {/* HERO — image + titre superposés */}
      {n.cover_image && (
        <div className="relative h-[46vh] lg:h-[64vh] overflow-hidden">
          <img src={n.cover_image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/95 via-ink/50 to-ink/10" />
          <div className="absolute inset-x-0 bottom-0">
            <div className="max-w-5xl mx-auto px-6 lg:px-12 pb-10 lg:pb-14 text-white">
              <Link to="/actualites" className="text-white/80 hover:text-gold text-sm flex items-center gap-1.5 mb-4">
                <ArrowLeft className="w-3.5 h-3.5" /> Toutes les actualités
              </Link>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {n.is_pinned && <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-wider bg-ink/85 text-gold px-2 py-1 rounded"><Pin className="w-3 h-3" /> Épinglé</span>}
                {n.is_featured && <span className="inline-flex items-center gap-1 text-[0.65rem] uppercase tracking-wider bg-gold text-ink px-2 py-1 rounded"><Star className="w-3 h-3" /> À la une</span>}
                <div className="eyebrow eyebrow-on-dark">{n.category}</div>
              </div>
              <h1 className="font-serif text-3xl lg:text-5xl leading-tight tracking-tight max-w-4xl">{n.title}</h1>
              <div className="mt-4 flex items-center gap-5 flex-wrap text-sm text-white/85">
                <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gold" /> {fmt(n.published_at)}</span>
                {n.location && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gold" /> {n.location}</span>}
                <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5 text-gold" /> {n.view_count || 0} vues</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 lg:px-0 py-14 lg:py-20">
        {/* Chapô */}
        <p className="font-serif text-2xl text-ink leading-snug italic border-l-2 border-gold pl-6">{n.excerpt}</p>

        {/* Corps */}
        <div className="mt-10 text-foreground/85 leading-[1.85] text-[1.06rem] whitespace-pre-line font-serif">
          {n.body}
        </div>

        {/* Vidéos YouTube */}
        {videos.length > 0 && (
          <div className="mt-14">
            <div className="eyebrow text-gold-dark">Vidéos</div>
            <div className="mt-3 divider-gold w-16" />
            <div className={`mt-6 grid ${videos.length > 1 ? "sm:grid-cols-2" : ""} gap-5`}>
              {videos.map((url, i) => {
                const id = ytId(url);
                if (!id) return null;
                return (
                  <div key={i} className="relative aspect-video rounded-sm overflow-hidden bg-ink shadow-lg" data-testid={`news-video-${i}`}>
                    <iframe
                      src={`https://www.youtube.com/embed/${id}`}
                      title={`Vidéo ${i + 1}`}
                      className="w-full h-full"
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Galerie */}
        {gallery.length > 0 && (
          <div className="mt-14">
            <div className="eyebrow text-gold-dark">Galerie photos</div>
            <div className="mt-3 divider-gold w-16" />
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {gallery.map((img, i) => (
                <a key={i} href={img.url} target="_blank" rel="noreferrer"
                   className="block aspect-[4/3] overflow-hidden rounded-sm bg-ink/5 group" data-testid={`news-gallery-${i}`}>
                  <img src={img.url} alt={img.caption || `${n.title} — ${i + 1}`}
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {(n.tags || []).length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2">
            {n.tags.map((t) => (
              <span key={t} className="text-[0.7rem] uppercase tracking-wider bg-white border border-border px-2.5 py-1 rounded-full text-foreground/65">
                {t}
              </span>
            ))}
          </div>
        )}

        {/* Boutons de partage */}
        <div className="mt-14 border-t border-border pt-8">
          <div className="flex items-center gap-3 mb-4">
            <Share2 className="w-4 h-4 text-gold-dark" />
            <span className="eyebrow text-gold-dark">Partager cet article</span>
          </div>
          <div className="flex flex-wrap items-center gap-2.5" data-testid="news-share-buttons">
            <ShareBtn kind="whatsapp" url={shareUrl} text={n.title} testid="share-whatsapp" />
            <ShareBtn kind="facebook" url={shareUrl} text={n.title} testid="share-facebook" />
            <ShareBtn kind="twitter" url={shareUrl} text={n.title} testid="share-twitter" />
            <ShareBtn kind="linkedin" url={shareUrl} text={n.title} testid="share-linkedin" />
            <ShareBtn kind="email" url={shareUrl} text={n.title} excerpt={n.excerpt} testid="share-email" />
            <button onClick={copyLink}
                    className="inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-border hover:border-ink text-foreground/75 hover:text-ink rounded-sm text-sm font-semibold transition-colors"
                    data-testid="share-copy">
              {copied ? <><Check className="w-3.5 h-3.5 text-emerald-600" /> Copié !</> : <><CopyIcon className="w-3.5 h-3.5" /> Copier le lien</>}
            </button>
          </div>
        </div>
      </div>

      {/* Suggestions */}
      {related.length > 0 && (
        <section className="bg-white border-t border-border py-14 lg:py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="eyebrow text-gold-dark">À lire aussi</div>
            <h2 className="font-serif text-3xl lg:text-4xl text-ink mt-2 leading-tight">Autres publications</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link key={r.id} to={`/actualites/${r.slug}`}
                      className="group block border border-border rounded-sm overflow-hidden hover:border-ink/30 transition-all bg-bone"
                      data-testid={`news-related-${r.slug}`}>
                  <div className="aspect-[16/10] overflow-hidden bg-ink/5">
                    {r.cover_image && <img src={r.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                  </div>
                  <div className="p-5">
                    <div className="eyebrow text-gold-dark text-[0.65rem]">{r.category} · {fmt(r.published_at)}</div>
                    <h3 className="font-serif text-xl text-ink leading-tight mt-2">{r.title}</h3>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-ink text-sm font-semibold group-hover:gap-3 transition-all">
                      Lire <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}

function upsertMeta(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content || "");
}

function ShareBtn({ kind, url, text, excerpt, testid }) {
  const encoded = encodeURIComponent(url);
  const encText = encodeURIComponent(text || "");
  const encExcerpt = encodeURIComponent(excerpt || "");
  let href = "#";
  let label = "";
  let Icon = Share2;
  let color = "text-foreground/75 hover:text-ink";
  switch (kind) {
    case "whatsapp":
      href = `https://wa.me/?text=${encText}%20-%20${encoded}`;
      label = "WhatsApp";
      Icon = WhatsAppIcon;
      color = "text-[#25D366] hover:text-[#128C7E]";
      break;
    case "facebook":
      href = `https://www.facebook.com/sharer/sharer.php?u=${encoded}`;
      label = "Facebook";
      Icon = Facebook;
      color = "text-[#1877F2] hover:text-[#0d5db3]";
      break;
    case "twitter":
      href = `https://twitter.com/intent/tweet?url=${encoded}&text=${encText}`;
      label = "X (Twitter)";
      Icon = TwitterXIcon;
      color = "text-ink hover:text-black";
      break;
    case "linkedin":
      href = `https://www.linkedin.com/sharing/share-offsite/?url=${encoded}`;
      label = "LinkedIn";
      Icon = Linkedin;
      color = "text-[#0A66C2] hover:text-[#004182]";
      break;
    case "email":
      href = `mailto:?subject=${encText}&body=${encExcerpt}%20-%20${encoded}`;
      label = "Email";
      Icon = Mail;
      color = "text-foreground/75 hover:text-ink";
      break;
    default:
      break;
  }
  return (
    <a href={href} target={kind === "email" ? undefined : "_blank"} rel="noreferrer"
       className={`inline-flex items-center gap-2 px-3.5 py-2 bg-white border border-border hover:border-current rounded-sm text-sm font-semibold transition-colors ${color}`}
       data-testid={testid}>
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
}

/* Icons non standards */
function WhatsAppIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}
function TwitterXIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
